import React from "react";
import { Clock } from "lucide-react";
import { tenantLocale, tenantTz } from "../lib/company";

/**
 * Compact chronological trail of what happened to a job — the "what did
 * the driver just do?" strip that lives inside the Job Detail Sheet.
 *
 * Feb 2026 — one place for the dispatcher to see the driver's whole
 * motion:  Assigned → Accepted → En route → Opened FR/AA app → On scene
 * (geofence vs manual) → Photos uploaded → Awaiting partner close →
 * Completed. Every event is stamped on the job doc by different code
 * paths (see /app/backend/job_status.py and /app/backend/telematics.py);
 * this component just displays whatever timestamps are present, in the
 * canonical order.
 *
 * Design: single dense column, small font, tabular alignment. No
 * separator lines — just muted text. Events without a timestamp are
 * simply omitted (no "—" placeholders — those add visual noise).
 */

// Format an ISO timestamp uniformly as "d MMM · hh:mm" — always includes
// the date so the timeline can be read alongside the Created/Completed
// block above without wondering "which day was that?". Feb 2026 —
// previously same-day timestamps were shortened to "hh:mm" which mixed
// awkwardly with older rows in the same timeline (e.g. Assigned "13:50"
// next to Accepted "3 Sept, 17:10" — user report).
function fmt(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleString(tenantLocale(), { timeZone: tenantTz(),
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
  } catch {
    return null;
  }
}

export default function JobTimeline({ job }) {
  if (!job) return null;

  const partnerName =
    (job.source === "servicenow_api" || job.source === "servicenow_image") ? "First Rescue"
      : (job.source === "aa_workforms" || job.source === "aa_sms") ? "AA"
        : "partner app";

  // Build a list in a single pass — order matters, so we push in canonical
  // sequence and skip entries without a timestamp.
  //
  // Feb 2026: "Assigned" now REQUIRES a genuine `assigned_at` stamp — the
  // old fallback to `updated_at` produced drifting timestamps that
  // landed AFTER completion because `updated_at` gets bumped on every
  // later mutation (photo upload, storage clock, archive, email send).
  // Legacy jobs from before the backend fix simply don't render the
  // Assigned row — the rest of the timeline still tells the story.
  const events = [];
  if (job.assigned_at)
    events.push({ label: "Assigned", at: job.assigned_at });
  if (job.accepted_at)
    events.push({ label: `Accepted${job.accepted_by ? " by " + job.accepted_by : ""}`, at: job.accepted_at });
  // Oct 2026: the driver can accept in the PARTNER's app instead of ours, in
  // which case nothing was ever shown here and the board looked idle while
  // the truck was en route. Not the same thing as an acceptance in TowHook,
  // so it says where it came from.
  if (job.aa_driver_engaged_at && !job.accepted_at)
    events.push({ label: `Driver engaged in ${partnerName}`, at: job.aa_driver_engaged_at });
  // A return to the queue was previously invisible — the job simply appeared
  // to have been assigned twice.
  if (job.returned_to_queue_at)
    events.push({
      label: `Returned to queue${job.returned_to_queue_by ? " by " + job.returned_to_queue_by : ""}`,
      at: job.returned_to_queue_at,
    });
  if (job.en_route_at)
    events.push({ label: "En route", at: job.en_route_at });
  if (job.partner_opened_at)
    events.push({ label: `Opened ${partnerName}`, at: job.partner_opened_at });
  if (job.on_site_at) {
    const via = job.auto_arrival_at
      ? ` (geofence · ${job.auto_arrival_distance_m || "?"} m)`
      : " (driver marked)";
    events.push({ label: `On scene${via}`, at: job.on_site_at });
  } else if (job.auto_arrival_at) {
    // Rare edge case — auto-arrival stamped but on_site_at wasn't set on
    // the mirror path. Show it anyway so dispatchers aren't blind.
    events.push({
      label: `On scene (geofence · ${job.auto_arrival_distance_m || "?"} m)`,
      at: job.auto_arrival_at,
    });
  }
  const photoCount = Array.isArray(job.photos) ? job.photos.length : 0;
  if (photoCount > 0) {
    // Use the most recent photo's timestamp so this event moves after
    // each fresh upload.
    const latestPhoto = job.photos
      .map((p) => p.uploaded_at || p.taken_at)
      .filter(Boolean)
      .sort()
      .pop();
    if (latestPhoto) events.push({ label: `${photoCount} photo${photoCount === 1 ? "" : "s"} uploaded`, at: latestPhoto });
  }
  if (job.partner_handover_at)
    events.push({
      label: `Awaiting ${partnerName} close${job.partner_handover_by ? " · " + job.partner_handover_by : ""}`,
      at: job.partner_handover_at,
    });
  if (job.completed_at)
    events.push({
      label: job.status === "cancelled" ? "Cancelled" : "Completed",
      at: job.completed_at,
    });
  if (job.last_emailed_at)
    events.push({ label: `Emailed insurer${job.last_emailed_by ? " · " + job.last_emailed_by : ""}`, at: job.last_emailed_at });

  if (events.length === 0) return null;

  // Chronological, not the order we happened to push them in. A job that was
  // returned to the queue and reassigned interleaves its events, and the old
  // fixed order rendered "assigned 11:05" above "accepted 10:18".
  events.sort((a, b) => String(a.at).localeCompare(String(b.at)));

  return (
    <div
      className="rounded-md border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3"
      data-testid="job-timeline"
    >
      <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-2">
        <Clock className="h-3.5 w-3.5" />
        Driver activity
      </div>
      <ol className="space-y-1">
        {events.map((ev, i) => (
          <li
            key={`${ev.label}-${ev.at}-${i}`}
            className="flex items-center justify-between gap-3 text-xs text-slate-700 dark:text-slate-200 font-mono tabular-nums"
            data-testid={`job-timeline-event-${i}`}
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="h-1.5 w-1.5 rounded-full bg-slate-400 dark:bg-slate-500 shrink-0" />
              <span className="truncate">{ev.label}</span>
            </span>
            <span className="text-slate-500 dark:text-slate-400 shrink-0">{fmt(ev.at)}</span>
          </li>
        ))}
      </ol>
    </div>
  );
}
